import { environment } from "../config/environment";

interface TokenResponse {
  token: string;
  expiresIn: number;
  tokenType: string;
}

interface TokenData {
  token: string;
  expiresAt: number;
}

class TokenManager {
  private tokenData: TokenData | null = null;
  private refreshPromise: Promise<string> | null = null;
  private refreshTimer: NodeJS.Timeout | null = null;

  constructor() {
    // Load token from sessionStorage on initialization
    this.loadTokenFromStorage();
  }

  /**
   * Get a valid token, refreshing if necessary
   */
  async getToken(): Promise<string> {
    // If we have a valid token, return it
    if (this.isTokenValid()) {
      return this.tokenData!.token;
    }

    // If we're already refreshing, wait for that to complete
    if (this.refreshPromise) {
      return this.refreshPromise;
    }

    // Start a new refresh
    this.refreshPromise = this.refreshToken();
    try {
      const token = await this.refreshPromise;
      return token;
    } finally {
      this.refreshPromise = null;
    }
  }

  /**
   * Force refresh the token
   */
  async refreshToken(): Promise<string> {
    try {
      console.log("Fetching new JWT token...");

      const response = await fetch(`${environment.apiUrl}/token`, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
        },
        cache: "no-cache",
      });

      if (!response.ok) {
        throw new Error(
          `Token fetch failed: ${response.status} ${response.statusText}`
        );
      }

      const data: TokenResponse = await response.json();

      if (!data.token) {
        throw new Error("No token received from server");
      }

      // Calculate expiry time
      const expiresAt = Date.now() + data.expiresIn * 1000;

      // Store token data
      this.tokenData = {
        token: data.token,
        expiresAt,
      };

      // Save to sessionStorage
      this.saveTokenToStorage();

      // Set up auto-refresh timer (refresh 5 minutes before expiry)
      this.setupRefreshTimer();

      console.log(
        `Token refreshed successfully, expires at: ${new Date(
          expiresAt
        ).toISOString()}`
      );

      return data.token;
    } catch (error) {
      console.error("Failed to refresh token:", error);
      this.clearToken();
      throw error;
    }
  }

  /**
   * Check if current token is valid (not expired and not expiring soon)
   */
  private isTokenValid(): boolean {
    if (!this.tokenData) {
      return false;
    }

    const now = Date.now();
    const timeUntilExpiry = this.tokenData.expiresAt - now;

    // Consider token invalid if it expires in less than 5 minutes
    return timeUntilExpiry > 5 * 60 * 1000;
  }

  /**
   * Set up automatic token refresh
   */
  private setupRefreshTimer(): void {
    if (this.refreshTimer) {
      clearTimeout(this.refreshTimer);
    }

    if (!this.tokenData) {
      return;
    }

    const now = Date.now();
    const timeUntilRefresh = this.tokenData.expiresAt - now - 5 * 60 * 1000; // 5 minutes before expiry

    if (timeUntilRefresh > 0) {
      this.refreshTimer = setTimeout(() => {
        console.log("Auto-refreshing token...");
        this.refreshToken().catch((error) => {
          console.error("Auto-refresh failed:", error);
        });
      }, timeUntilRefresh);
    }
  }

  /**
   * Load token from sessionStorage
   */
  private loadTokenFromStorage(): void {
    try {
      const stored = sessionStorage.getItem("jwt_token");
      if (stored) {
        this.tokenData = JSON.parse(stored);

        // Check if token is still valid
        if (this.isTokenValid()) {
          this.setupRefreshTimer();
        } else {
          this.clearToken();
        }
      }
    } catch (error) {
      console.warn("Failed to load token from storage:", error);
      this.clearToken();
    }
  }

  /**
   * Save token to sessionStorage
   */
  private saveTokenToStorage(): void {
    try {
      if (this.tokenData) {
        sessionStorage.setItem("jwt_token", JSON.stringify(this.tokenData));
      }
    } catch (error) {
      console.warn("Failed to save token to storage:", error);
    }
  }

  /**
   * Clear token and stop refresh timer
   */
  private clearToken(): void {
    this.tokenData = null;
    if (this.refreshTimer) {
      clearTimeout(this.refreshTimer);
      this.refreshTimer = null;
    }
    sessionStorage.removeItem("jwt_token");
  }

  /**
   * Get authorization header value
   */
  async getAuthorizationHeader(): Promise<string> {
    const token = await this.getToken();
    return `Bearer ${token}`;
  }

  /**
   * Check if we have a token (valid or not)
   */
  hasToken(): boolean {
    return this.tokenData !== null;
  }

  /**
   * Force clear all token data (useful for logout or errors)
   */
  clearAll(): void {
    this.clearToken();
  }
}

// Export singleton instance
export const tokenManager = new TokenManager();
export default tokenManager;
